/**
 * Job header component
 * Header section for job details page
 */

import { Space, Typography, Button, Tooltip, Tag, Dropdown, Descriptions } from 'antd';
import type { MenuProps } from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  MoreOutlined,
  CopyOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { JobStatusBadge } from '@/components/common/StatusBadge';
import type { Job } from '@/types/job';
import { formatDate, formatDuration, formatRelativeTime } from '@/utils/formatters';
import { cellSizeToZoom } from '@/utils/mapUtils';

const { Title, Text } = Typography;

interface JobHeaderProps {
  job: Job;
  onPause?: () => void;
  onResume?: () => void;
  onRetry?: () => void;
  onDelete?: () => void;
  onExport?: () => void;
  onDuplicate?: () => void;
  loading?: {
    pause?: boolean;
    resume?: boolean;
    retry?: boolean;
    delete?: boolean;
    export?: boolean;
  };
}

export function JobHeader({
  job,
  onPause,
  onResume,
  onRetry,
  onDelete,
  onExport,
  onDuplicate,
  loading = {},
}: JobHeaderProps) {
  const navigate = useNavigate();

  const getDuration = () => {
    if (!job.startedAt) return null;
    if (job.completedAt) {
      return formatDuration(
        new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime()
      );
    }
    return formatRelativeTime(job.startedAt);
  };

  const getMenuItems = (): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (onDuplicate) {
      items.push({
        key: 'duplicate',
        icon: <CopyOutlined />,
        label: 'Duplicate Job',
        onClick: onDuplicate,
      });
    }

    items.push({ type: 'divider' });

    if (onDelete) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete Job',
        danger: true,
      });
    }

    return items;
  };

  return (
    <div>
      {/* Top row with back button and title */}
      <div style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/jobs')}
          style={{ marginLeft: -8, marginBottom: 8 }}
        >
          Back to Jobs
        </Button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Space align="center" size={12}>
              <Title level={3} style={{ margin: 0 }}>
                {job.searchQuery}
              </Title>
              <JobStatusBadge status={job.status} />
            </Space>
            {job.name && (
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                {job.name}
              </Text>
            )}
          </div>

          {/* Actions */}
          <Space size={8}>
            {job.status === 'running' && onPause && (
              <Button
                icon={<PauseCircleOutlined />}
                onClick={onPause}
                loading={loading.pause}
              >
                Pause
              </Button>
            )}

            {job.status === 'paused' && onResume && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={onResume}
                loading={loading.resume}
              >
                Resume
              </Button>
            )}

            {job.status === 'failed' && onRetry && (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={onRetry}
                loading={loading.retry}
              >
                Retry Failed
              </Button>
            )}

            {job.status === 'completed' && job.placesFound > 0 && onExport && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={onExport}
                loading={loading.export}
              >
                Export Results
              </Button>
            )}

            <Dropdown
              menu={{ items: getMenuItems() }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </div>
      </div>

      {/* Job metadata */}
      <Descriptions
        size="small"
        column={{ xs: 1, sm: 2, md: 4 }}
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Created">
          <Tooltip title={formatDate(job.createdAt, 'full')}>
            {formatRelativeTime(job.createdAt)}
          </Tooltip>
        </Descriptions.Item>

        {job.startedAt && (
          <Descriptions.Item label="Started">
            <Tooltip title={formatDate(job.startedAt, 'full')}>
              {formatRelativeTime(job.startedAt)}
            </Tooltip>
          </Descriptions.Item>
        )}

        {job.completedAt && (
          <Descriptions.Item label="Completed">
            <Tooltip title={formatDate(job.completedAt, 'full')}>
              {formatRelativeTime(job.completedAt)}
            </Tooltip>
          </Descriptions.Item>
        )}

        {getDuration() && (
          <Descriptions.Item label="Duration">
            {getDuration()}
          </Descriptions.Item>
        )}

        <Descriptions.Item label="Grid Cells">
          {job.totalCells} cells
        </Descriptions.Item>

        <Descriptions.Item label="Zoom Level">
          {job.cellSizeKm ? cellSizeToZoom(job.cellSizeKm) : '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}

export default JobHeader;
